import { type ApplicationModelConstructor } from 'components/framework/ApplicationModel';
import type ApplicationModel from 'components/framework/ApplicationModel';
import Fragment from 'components/framework/Fragment';
import { camelCase } from 'lodash';
import BookmarksRecordPosition from '../BookmarksRecordPosition';
import CardLayout from '../CardLayout';
import CustomFieldDefinition from '../CustomFieldDefinition';
import Epic from '../Epic';
import Feature from '../Feature';
import Idea from '../Idea';
import Initiative from '../Initiative';
import Note from '../Note';
import PivotCell from '../PivotCell';
import PivotColumn from '../PivotColumn';
import PivotRow from '../PivotRow';
import Project from '../Project';
import Requirement from '../Requirement';
import ScoreFact from '../ScoreFact';
import ScoringSystemMetric from '../ScoringSystemMetric';
import User from '../User';
import Workflow from '../Workflow';
import WorkflowStatus from '../WorkflowStatus';
import ScreenDefinition from '../ScreenDefinition';
import ScreenDefinitionControl from '../ScreenDefinitionControl';
import {
  type OrderDirection,
  type PivotColumnWidth,
  type RecordId,
  type ReprioritizeByTypeEnum,
} from '../generated/types';
import type Query from 'components/framework/Query';
import {
  type PivotColumnWidthType,
  type PrioritizableRecordType,
} from 'components/priority_list/types';
import type LineBreak from '../LineBreak';
import PriorityListUnprioritizedRecord from '../PriorityListUnprioritizedRecord';
import Release from '../Release';

export const SHARED_BOOKMARK_COLUMNS = [
  'id',
  'filters',
  'joins',
  'columnTables',
  'projectId',
  'advancedFilters',
  'advancedFiltersParsed',
  'userAllowedToPublishRanks',
  'hasShippableRecords',
];

export const SHARED_BOOKMARK_MERGE_COLUMNS = {
  columns: PivotColumn.select(['title', 'key']),
  scoringSystemMetrics: ScoringSystemMetric.selectAll().merge({
    description: Note.select(['htmlBody']),
  }),
  columnWidths: ['columnKey', 'width'],
  publishableCustomFields: CustomFieldDefinition.select(['key', 'name']),
  lastPublishedField: CustomFieldDefinition.select(['key', 'name']),
  lineBreaks: ['rank', 'title'],
};

const DEFAULT_PRIORITIZED_COLUMNS = [
  'id',
  'name',
  'path',
  'score',
  'scoringSystemId',
  'projectId',
];
const DEFAULT_PRIORITIZED_MERGE_COLUMNS = {
  bookmarksRecordPositions: BookmarksRecordPosition.select([
    'bookmarkType',
    'position',
    'secondaryScope',
    'recordType',
    'recordId',
  ]),
  scoreFacts: ScoreFact.select([
    'id',
    'value',
    'equationActive',
    'equationType',
  ]).merge({
    scoringSystemMetric: ScoringSystemMetric.selectAll().merge({
      description: Note.select(['htmlBody']),
    }),
  }),
  assignedToUser: User.select(['id', 'name', 'avatarUrl']),
  project: Project.select(['name', 'id']).merge({ permissions: ['update'] }),
  workflowStatus: WorkflowStatus.select(['name', 'color', 'id']),
  screenDefinition: ScreenDefinition.select(['id']).merge({
    screenDefinitionControls: ScreenDefinitionControl.select([
      'id',
      'fieldId',
      'readOnly',
    ]).where({ fieldId: 'status' }),
  }),
};

export const projectWorkflowScope = (
  types: (
    | Idea['typename']
    | Feature['typename']
    | Epic['typename']
    | Initiative['typename']
    | Requirement['typename']
    | Release['typename']
  )[]
) => {
  return Project.select('id').merge(
    types.reduce((acc, type) => {
      acc[`${type.toLowerCase()}Workflow`] = Workflow.select([
        'id',
        'transitionsOnly',
      ]).merge({
        workflowStatuses: [
          'id',
          'name',
          'position',
          'color',
          'internalMeaning',
        ],
      });
      return acc;
    }, {})
  );
};

export interface HasBookmarkPriorityListStatic<
  M extends ApplicationModel & HasBookmarksPriorityList
> {
  recordTypeName: string;
  buildPrioritizedRecordListScope(
    this: HasBookmarkPriorityListStatic<M>,
    args: { page: number; per: number }
  ): Query<any, any>;

  buildUnprioritizedRecordScope(
    this: HasBookmarkPriorityListStatic<M>,
    cardLayout: CardLayout
  ): Query<any, any>;

  buildScope(this: HasBookmarkPriorityListStatic<M>): Query<any, M>;

  buildPrioritizedRecordScope(recordId: string): Query<any, any>;

  buildUnprioritizedRecordListScope(cardLayout: CardLayout): Query<any, any>;

  bulkPrioritizeRecords(options: {
    bookmarkId: string;
    projectId?: string;
    prev?: any;
    next?: any;
    placement?: any;
    searchQuery: any;
  }): Promise<void>;

  reprioritizeRecords(options: {
    bookmarkId: string;
    reprioritizeBy: ReprioritizeByTypeEnum['type'];
    orderDirection: OrderDirection['type'];
    scoringSystemMetricId?: string;
  }): Promise<void>;

  readonly bookmarkType: string;
  readonly recordType: PrioritizableRecordType;

  getWorkflowsForProjects?(projectIds: string[]): any;

  updateScoreFacts(record: ApplicationModel, scoreFactAttrs): Promise<void>;

  new (...args: any[]): M;
}

export interface HasBookmarksPriorityList {
  resetFilters(): Promise<void>;
  updateColumns(columns: PivotColumn[]): Promise<void>;
  updateColumnWidths(
    columnWidths: PivotColumnWidthType,
    persist: boolean
  ): Promise<void>;
  getLineBreak(rank: number | string): LineBreak | undefined;
}

export function HasBookmarksPriorityList<M extends typeof ApplicationModel>(
  originalClass: {
    recordType:
      | typeof Feature
      | typeof Epic
      | typeof Requirement
      | typeof Idea
      | typeof Initiative;
    buildUnprioritizedRecordScope?(cardLayout: CardLayout): any;
    buildScope?(): any;
    getWorkflowsForProjects?(projectIds: string[]): any;
    new (...args: any[]): ApplicationModel & {
      id: string;
      projectId: string;
      columnWidths: Array<PivotColumnWidth>;
      lineBreaks: Array<LineBreak>;
    };
  } & ApplicationModelConstructor<ApplicationModel>
): any {
  const newClass = class BookmarksPriorityList
    extends originalClass
    implements HasBookmarksPriorityList
  {
    static get recordTypeName() {
      return this.recordType.typename;
    }

    static buildPrioritizedRecordListScope(
      this: M & typeof BookmarksPriorityList,
      { page, per }
    ) {
      let recordMerge = this.recordType
        .select(DEFAULT_PRIORITIZED_COLUMNS)
        .merge(DEFAULT_PRIORITIZED_MERGE_COLUMNS);

      const associationQuery = (model: typeof ApplicationModel) =>
        model.select('id', 'projectId').merge({
          workflowStatus: WorkflowStatus.select(['name', 'color', 'id']),
        });

      if (this.recordType === Idea) {
        const queries = [Epic, Feature, Initiative, Requirement, Release].map(
          model => associationQuery(model)
        );
        recordMerge = recordMerge.merge({
          promotable: queries.reduce((scope, query) => scope.union(query)),
        });
      }

      if (this.recordType === Feature) {
        recordMerge = recordMerge.merge({
          epic: associationQuery(Epic),
        });
      }

      if (this.recordType === Feature || this.recordType === Epic) {
        recordMerge = recordMerge.merge({
          initiative: associationQuery(Initiative),
        });
      }

      return this.select(['id', 'totalPrioritizedRecords']).merge({
        prioritizedRows: PivotRow.merge({
          cells: PivotCell.select(['value']).merge({
            pivotColumn: PivotColumn.select(['key']),
          }),
        })
          .merge({
            record: recordMerge.union(),
          })
          .page(page)
          .per(per)
          .stats(['isLastPage', 'totalPages']),
      });
    }

    static buildPrioritizedRecordScope(
      this: M & typeof BookmarksPriorityList,
      recordId: string
    ) {
      let recordMerge = this.recordType
        .select(DEFAULT_PRIORITIZED_COLUMNS)
        .merge(DEFAULT_PRIORITIZED_MERGE_COLUMNS);

      if (this.recordType === Feature) {
        recordMerge = recordMerge.merge({
          epic: Epic.select(['id']).merge({
            workflowStatus: WorkflowStatus.select(['name', 'color', 'id']),
            project: Project.select(['id']),
          }),
        });
      }

      if (this.recordType === Feature || this.recordType === Epic) {
        recordMerge = recordMerge.merge({
          initiative: Initiative.select(['id']).merge({
            workflowStatus: WorkflowStatus.select(['name', 'color', 'id']),
            project: Project.select(['id']),
          }),
        });
      }

      return this.select([])
        .merge({
          prioritizedRow: PivotRow.merge({
            cells: PivotCell.select(['value']).merge({
              pivotColumn: PivotColumn.select(['key']),
            }),
          }).merge({
            record: recordMerge.union(),
          }),
        })
        .argument({ prioritizedRowId: recordId });
    }

    static buildUnprioritizedRecordListScope(
      this: M & typeof BookmarksPriorityList,
      cardLayout: CardLayout
    ) {
      return this.select('id').merge({
        unprioritizedRecords: PriorityListUnprioritizedRecord.select([
          'prioritizable',
        ]).merge({
          record: this.buildUnprioritizedRecordScope(cardLayout).union(),
        }),
      });
    }

    static async bulkPrioritizeRecords(
      this: M & typeof BookmarksPriorityList,
      { bookmarkId, projectId, prev, next, placement, searchQuery }
    ) {
      let fragment = new Fragment('bulkPrioritizeRecords').argument(
        'id',
        bookmarkId
      );

      if (projectId) {
        fragment = fragment.argument('projectId', projectId);
      }
      if (prev) {
        fragment = fragment.argument('placeAfterId', prev.id);
      }
      if (next) {
        fragment = fragment.argument('placeBeforeId', next.id);
      }
      if (placement) {
        fragment = fragment.argument('placement', placement);
      }
      if (searchQuery) {
        fragment = fragment.argument('searchQuery', searchQuery);
      }
      await this.mutate(fragment);
    }

    static async reprioritizeRecords(
      this: M & typeof BookmarksPriorityList,
      { bookmarkId, reprioritizeBy, orderDirection, scoringSystemMetricId }
    ) {
      let fragment = new Fragment('reprioritizeRecords')
        .argument('id', bookmarkId)
        .argument('reprioritizeBy', reprioritizeBy, true)
        .argument('orderDirection', orderDirection, true);

      if (scoringSystemMetricId) {
        fragment = fragment.argument(
          'scoringSystemMetricId',
          scoringSystemMetricId
        );
      }

      await this.mutate(fragment);
    }

    static async updateScoreFacts(record, scoreFactAttrs) {
      record.flagDirty('score');

      try {
        await record.mutate(
          new Fragment(`update${this.recordType.name}`)
            .fragment(
              new Fragment(this.recordType.name.toLowerCase())
                .attr('score')
                .fragment(new Fragment('scoreFacts').attr('value'))
            )
            .argument('id', record.id)
            .argument(
              'attributes',
              { scoreFacts: scoreFactAttrs },
              `${this.recordType.name}Attributes!`
            )
        );
      } finally {
        record.dirtyAttributes.delete('score');
      }
    }

    async updateColumns(columns: PivotColumn[]) {
      await this.mutate(
        new Fragment(this.modelConstructor.updateFragmentName)
          .argument('id', this.id)
          .argument(
            'attributes',
            { columns: columns.map(col => col.key) },
            'PriorityListAttributes!'
          )
      );
    }

    async resetFilters() {
      await this.mutate(
        new Fragment(this.modelConstructor.updateFragmentName)
          .argument('id', this.id)
          .argument(
            'attributes',
            {
              clearFilters: true,
            },
            'PriorityListAttributes!'
          )
      );
    }

    async updateFilter(filter, newFilterConfig) {
      await this.mutate(
        new Fragment(this.modelConstructor.updateFragmentName)
          .argument('id', this.id)
          .argument(
            'attributes',
            {
              filters: {
                [camelCase(filter.scoped_name)]: newFilterConfig,
              },
            },
            'PriorityListAttributes!'
          )
      );
    }

    /**
     * @param columnWidths Column widths of the bookmark
     * @param persist Whether or not to persist the column widths
     */
    async updateColumnWidths(
      columnWidths: PivotColumnWidthType,
      persist: boolean
    ) {
      this.columnWidths = Object.keys(columnWidths).map(columnKey => ({
        columnKey,
        width: Math.round(columnWidths[columnKey]),
      }));

      if (persist) {
        await this.save();
      }
    }

    /**
     * Load all the users available to this team
     */
    async availableUsers() {
      if (!this.local.users)
        this.local.users = await User.select('id', 'name')
          .where({ projectId: this.projectId })
          .all();
      return this.local.users;
    }

    getLineBreak(rank: number | string) {
      return this.lineBreaks.find(lineBreak => lineBreak.rank === Number(rank));
    }

    // This is a magic method called at
    // https://github.com/aha-app/aha-app/blob/08d5a3c434f4a3eb8c202534e1e77e2bbd6db803/app/assets/components/framework/ApplicationModel.ts#L704-L70
    // Without it setting lineBreaks causes a callstack exceeded error
    lineBreaksForQuery(value) {
      return this.lineBreaks.map(({ rank, title }) => ({ rank, title }));
    }
  };

  if (!('getWorkflowsForProjects' in originalClass)) {
    newClass.getWorkflowsForProjects = async function (
      this: typeof originalClass,
      projectIds: RecordId[]
    ) {
      return projectWorkflowScope([this.recordType.typename])
        .where({ id: projectIds })
        .all();
    };
  }

  if (!('buildScope' in originalClass)) {
    newClass.buildScope = function (this: typeof originalClass) {
      const recordTypeName = this.recordType.typename;
      // Type check, buildCardLayoutScope does not accept Idea and
      // BookmarksIdeaPriorityList implements its own buildScope
      if (recordTypeName === 'Idea') {
        return;
      }

      return this.select(SHARED_BOOKMARK_COLUMNS)
        .merge(SHARED_BOOKMARK_MERGE_COLUMNS)
        .merge({
          cardLayout: CardLayout.buildCardLayoutScope({
            recordTypes: [this.recordType.typename],
          }),
          defaultCardLayout: CardLayout.buildCardLayoutScope({
            recordTypes: [this.recordType.typename],
          }),
        });
    };
  }
  if (!('buildUnprioritizedRecordScope' in originalClass)) {
    newClass.buildUnprioritizedRecordScope = function (cardLayout: CardLayout) {
      const query = this.recordType
        .select(['id', 'path', 'name', 'projectId'])
        .merge({
          project: Project.select(['id', 'name']).merge({
            permissions: ['update'],
          }),
          workflowStatus: ['color', 'name'],
          permissions: ['update'],
        });

      return cardLayout
        .recordLayout(this.recordType.typename)
        .expanded.buildScope(query);
    };
  }

  return newClass;
}
